import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  TrendingUp, 
  TrendingDown, 
  Building2, 
  Users, 
  CreditCard, 
  AlertTriangle,
  DollarSign,
  Calendar,
  ArrowUpRight,
  Download,
  Filter,
  RefreshCw,
  Search,
  Eye,
  MoreHorizontal,
  Target,
  Activity,
  Zap,
  Globe,
  BarChart3,
  PieChart as PieChartIcon,
  FileSpreadsheet,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingDown as TrendingDownIcon,
  Crown
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  Legend,
  ComposedChart
} from 'recharts';

const UltraDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [realTimeData, setRealTimeData] = useState({});
  const [recentOrgs, setRecentOrgs] = useState([]);

  const API = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const [markingOverdue, setMarkingOverdue] = useState(false);
  const [overdueResult, setOverdueResult] = useState(null);
  const [sendingReengagement, setSendingReengagement] = useState(false);
  const [reengagementResult, setReengagementResult] = useState(null);

  const handleMarkOverdue = async () => {
    setMarkingOverdue(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/api/invoices/mark-overdue?send_reminders=true`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverdueResult(res.data);
      fetchDashboardData(); // refresh dashboard
      setTimeout(() => setOverdueResult(null), 5000);
    } catch (e) {
      console.error('Mark overdue error:', e);
    } finally {
      setMarkingOverdue(false);
    }
  };

  const handleSendReengagement = async () => {
    setSendingReengagement(true);
    setReengagementResult(null);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/api/ultra/send-reengagement-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReengagementResult(res.data);
      setTimeout(() => setReengagementResult(null), 8000);
    } catch (e) {
      setReengagementResult({ error: 'Failed to send emails. Check SendGrid configuration.' });
    } finally {
      setSendingReengagement(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Fetch real system stats every 30 seconds instead of random simulation
    const interval = setInterval(fetchSystemStats, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchSystemStats = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/ultra/system-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRealTimeData({
        lastUpdate: new Date().toLocaleTimeString('tr-TR'),
        activeUsers: res.data.active_users_24h ?? 0,
        systemLoad: res.data.system_load_pct ?? 0,
        realStats: res.data,
      });
    } catch {
      // Keep last known value on error
      setRealTimeData(prev => ({ ...prev, lastUpdate: new Date().toLocaleTimeString('tr-TR') }));
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    // Fetch both dashboard and system stats in parallel
    await fetchSystemStats();
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/ultra/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const kpis = res.data.kpis || {};
      const charts = res.data.charts || {};

      setStats({
        activeOrgs: { value: kpis.active_orgs || 0, change: 0, trend: 'up', target: 100, completion: Math.min(100, (kpis.active_orgs||0)) },
        mrr: { value: kpis.mrr || 0, change: 0, trend: kpis.mrr > 0 ? 'up' : 'neutral', currency: 'EUR', target: 50000, completion: Math.min(100, ((kpis.mrr||0)/500)) },
        trials: { value: kpis.trials || 0, change: 0, trend: 'down', target: 20, completion: Math.min(100, ((kpis.trials||0)/20)*100) },
        overdueInvoices: { value: kpis.overdue_invoices || 0, change: 0, trend: 'up', target: 0, completion: 0 },
        totalUsers: { value: kpis.total_users || 0, change: 0, trend: 'up', target: 500, completion: Math.min(100, ((kpis.total_users||0)/500)*100) },
        conversionRate: { value: kpis.trials > 0 ? ((kpis.active_orgs/kpis.total_users)*100).toFixed(1) : 0, change: 0, trend: 'up', target: 6.0, completion: 80.0 },
        avgRevenuePerUser: { value: kpis.total_users > 0 ? Math.round((kpis.mrr||0) / kpis.total_users) : 0, change: 0, trend: 'up', currency: 'EUR', target: 50, completion: 80.0 },
        systemUptime: { value: 99.9, change: 0.1, trend: 'up', target: 99.9, completion: 100 },
        totalBriefs: { value: kpis.total_briefs || 0 },
        totalProposals: { value: kpis.total_proposals || 0 },
      });

      // Map real weekly signups chart data
      const signupsRaw = charts.signups_weekly || [];
      const signupsData = signupsRaw.map((d, i) => ({
        week: d.week || `Hafta ${i+1}`,
        signups: d.count || 0,
        revenue: Math.floor((d.count || 0) * 120),
        conversion: (((d.count||0) * 4.2) / 100).toFixed(1),
      }));

      // Map real subs_by_role
      const roleColors = { EXHIBITOR: '#3B82F6', CONTRACTOR: '#10B981', SUPPLIER: '#F59E0B', ULTRA_ADMIN: '#8B5CF6' };
      const subsData = (charts.subs_by_role || []).map(s => ({
        name: s.role || 'Other',
        value: s.count || 0,
        color: roleColors[s.role] || '#6B7280',
        revenue: (s.count || 0) * 199,
      }));
      if (subsData.length === 0) {
        subsData.push(
          { name: 'Exhibitor', value: 0, color: '#3B82F6', revenue: 0 },
          { name: 'Contractor', value: 0, color: '#10B981', revenue: 0 },
        );
      }

      // Geo data — use real API data if available, else fallback
      const geoRaw = charts.geo_by_country || [];
      const geoColors = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#EC4899','#6B7280','#EF4444','#0EA5E9','#14B8A6','#F97316'];
      const geoData = geoRaw.length > 0
        ? geoRaw.map((g, i) => ({ country: g.role || 'Unknown', value: g.count, color: geoColors[i % geoColors.length] }))
        : [
            { country: 'Türkiye', value: 45, color: '#3B82F6' },
            { country: 'Germany', value: 20, color: '#10B981' },
            { country: 'UAE', value: 15, color: '#F59E0B' },
            { country: 'USA', value: 12, color: '#8B5CF6' },
            { country: 'Others', value: 8, color: '#6B7280' }
          ];
      // Performance chart: static pattern (system metrics need infrastructure agent, not frontend mock)
      const performanceData = [];
      for (let i = 23; i >= 0; i--) {
        const time = new Date(); time.setHours(time.getHours() - i);
        performanceData.push({ time: time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }), cpu: 32 + (i % 5) * 4, memory: 52 + (i % 7) * 3, requests: 80 + (i % 9) * 15 });
      }
      setChartData({ signups: signupsData, subscriptions: subsData, geography: geoData, performance: performanceData });

      // Fetch recent orgs for the bottom table
      try {
        const orgsRes = await axios.get(`${API}/api/ultra/organizations?limit=8&sort=created_desc`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const orgsData = (orgsRes.data.items || []).map(o => ({
          name: o.display_name || o.legal_name || 'Bilinmiyor',
          type: o.org_type || 'UNKNOWN',
          plan: o.plan_code || 'dynamic',
          mrr: o.subscription?.mrr || 0,
          date: o.created_at ? o.created_at.slice(0, 10) : '',
          status: (o.status || 'active').toLowerCase(),
          users: o.user_count || 1,
          lastActive: o.updated_at ? o.updated_at.slice(0, 10) : '',
        }));
        setRecentOrgs(orgsData);
      } catch { setRecentOrgs([]); }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      // Fallback to safe empty state
      setStats({ activeOrgs: {value:0,trend:'up'}, mrr: {value:0,currency:'EUR'}, trials: {value:0}, overdueInvoices: {value:0}, totalUsers: {value:0}, conversionRate: {value:0}, avgRevenuePerUser: {value:0}, systemUptime: {value:99.9}, totalBriefs: {value:0}, totalProposals: {value:0} });
      setChartData({ signups: [], subscriptions: [], geography: [], performance: [] });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('tr-TR').format(number);
  };

  const AdvancedKPICard = ({ title, value, change, trend, currency, suffix, icon: Icon, color = 'blue', target, completion }) => (
    <Card className="hover:shadow-lg transition-all duration-300 border-0 shadow-sm hover:shadow-blue-100/50">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${
          color === 'blue' ? 'from-blue-500 to-blue-600' :
          color === 'green' ? 'from-green-500 to-green-600' :
          color === 'yellow' ? 'from-yellow-500 to-yellow-600' :
          color === 'red' ? 'from-red-500 to-red-600' :
          color === 'purple' ? 'from-purple-500 to-purple-600' :
          'from-gray-500 to-gray-600'
        } shadow-lg`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="text-2xl font-bold text-gray-900">
            {suffix
              ? `${formatNumber(value)}${suffix}`
              : currency
                ? formatCurrency(value, currency)
                : formatNumber(value)}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              {trend === 'up' ? (
                <TrendingUp className="h-3 w-3 text-green-600" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-600" />
              )}
              <span className={`text-xs font-medium ${
                trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.abs(change)}%
              </span>
              <span className="text-xs text-gray-500">bu ay</span>
            </div>
            {target && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Hedef</div>
                <div className="text-xs font-medium text-gray-700">
                  {suffix
                    ? `${formatNumber(target)}${suffix}`
                    : currency
                      ? formatCurrency(target, currency)
                      : formatNumber(target)}
                </div>
              </div>
            )}
          </div>

          {completion && (
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Tamamlanma</span>
                <span className="text-xs font-medium text-gray-700">{completion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full bg-gradient-to-r ${
                    color === 'blue' ? 'from-blue-400 to-blue-600' :
                    color === 'green' ? 'from-green-400 to-green-600' :
                    color === 'yellow' ? 'from-yellow-400 to-yellow-600' :
                    color === 'red' ? 'from-red-400 to-red-600' :
                    color === 'purple' ? 'from-purple-400 to-purple-600' :
                    'from-gray-400 to-gray-600'
                  }`}
                  style={{ width: `${Math.min(completion, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const RealTimeCard = ({ title, value, icon: Icon, color = 'blue', lastUpdate }) => (
    <Card className="border-l-4 border-l-green-500 bg-gradient-to-r from-green-50 to-white">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {lastUpdate && (
              <p className="text-xs text-gray-500 flex items-center mt-1">
                <Activity className="h-3 w-3 mr-1" />
                Son güncelleme: {lastUpdate}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-full bg-gradient-to-br ${
            color === 'green' ? 'from-green-500 to-green-600' : 'from-blue-500 to-blue-600'
          }`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Sistem genel durumu ve istatistikleri</p>
          </div>
          <div className="animate-spin">
            <RefreshCw className="h-6 w-6 text-gray-400" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 -m-3 sm:-m-6 p-3 sm:p-6 min-h-screen" data-testid="ultra-dashboard">
      {/* Advanced Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Ultra Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Real-time system overview and advanced analytics • Son güncelleme: {realTimeData.lastUpdate || 'Yükleniyor...'}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Arama yap..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48 h-9"
              />
            </div>
            
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Son 7 Gün</option>
              <option value="30d">Son 30 Gün</option>
              <option value="90d">Son 90 Gün</option>
              <option value="1y">Son 1 Yıl</option>
            </select>
            
            <Button variant="outline" size="sm" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Excel İndir
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="h-4 w-4" />
              Gelişmiş Filtre
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarkOverdue}
              disabled={markingOverdue}
              className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
            >
              <AlertCircle className="h-4 w-4" />
              {markingOverdue ? 'İşleniyor...' : 'Gecikmiş Faturaları İşaretle'}
            </Button>
            <Button
              data-testid="send-reengagement-btn"
              size="sm"
              variant="outline"
              onClick={handleSendReengagement}
              disabled={sendingReengagement}
              className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              <Zap className="h-4 w-4" />
              {sendingReengagement ? 'Gönderiliyor...' : 'Re-engagement Email Gönder'}
            </Button>
            <Button size="sm" onClick={fetchDashboardData} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <RefreshCw className="h-4 w-4" />
              Yenile
            </Button>
          </div>
        </div>
      </div>

      {/* Overdue result banner */}
      {overdueResult && (
        <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
          <AlertCircle className="h-5 w-5 text-orange-600 shrink-0" />
          <p className="text-sm text-orange-800">
            <strong>{overdueResult.marked_overdue}</strong> fatura gecikmiş olarak işaretlendi.
            {overdueResult.reminders_sent > 0 && <> <strong>{overdueResult.reminders_sent}</strong> hatırlatma e-postası gönderildi.</>}
            {overdueResult.marked_overdue === 0 && ' Gecikmiş fatura bulunamadı.'}
          </p>
        </div>
      )}

      {/* Re-engagement result banner */}
      {reengagementResult && (
        <div className={`flex items-center gap-3 rounded-xl p-4 border ${reengagementResult.error ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
          <Zap className={`h-5 w-5 shrink-0 ${reengagementResult.error ? 'text-red-500' : 'text-purple-600'}`} />
          <p className={`text-sm ${reengagementResult.error ? 'text-red-800' : 'text-purple-800'}`}>
            {reengagementResult.error ? reengagementResult.error : (
              <><strong>{reengagementResult.emails_sent}</strong> re-engagement email gönderildi
              ({reengagementResult.users_checked} kullanıcı kontrol edildi,
              {reengagementResult.emails_skipped} atlandı).</>
            )}
          </p>
        </div>
      )}

      {/* Real-time Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RealTimeCard
          title="Aktif Kullanıcılar (Canlı)"
          value={realTimeData.activeUsers}
          icon={Users}
          color="green"
          lastUpdate={realTimeData.lastUpdate}
        />
        <RealTimeCard
          title="Sistem Yükü"
          value={`${realTimeData.systemLoad}%`}
          icon={Activity}
          color="blue"
          lastUpdate={realTimeData.lastUpdate}
        />
      </div>

      {/* Enhanced KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div data-testid="kpi-active-orgs">
          <AdvancedKPICard
            title="Aktif Organizasyonlar"
            value={stats?.activeOrgs.value}
            change={stats?.activeOrgs.change}
            trend={stats?.activeOrgs.trend}
            target={stats?.activeOrgs.target}
            completion={stats?.activeOrgs.completion}
            icon={Building2}
            color="blue"
          />
        </div>
        <div data-testid="kpi-mrr">
          <AdvancedKPICard
            title="Aylık Yinelenen Gelir (MRR)"
            value={stats?.mrr.value}
            change={stats?.mrr.change}
            trend={stats?.mrr.trend}
            currency={stats?.mrr.currency}
            target={stats?.mrr.target}
            completion={stats?.mrr.completion}
            icon={DollarSign}
            color="green"
          />
        </div>
        <div data-testid="kpi-trials">
          <AdvancedKPICard
            title="Deneme Sürümü Hesapları"
            value={stats?.trials.value}
            change={stats?.trials.change}
            trend={stats?.trials.trend}
            target={stats?.trials.target}
            completion={stats?.trials.completion}
            icon={Calendar}
            color="yellow"
          />
        </div>
        <div data-testid="kpi-overdue">
          <AdvancedKPICard
            title="Geciken Faturalar"
            value={stats?.overdueInvoices.value}
            change={stats?.overdueInvoices.change}
            trend={stats?.overdueInvoices.trend}
            target={stats?.overdueInvoices.target}
            completion={100 - stats?.overdueInvoices.completion}
            icon={AlertTriangle}
            color="red"
          />
        </div>
      </div>

      {/* Additional KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AdvancedKPICard
          title="Toplam Kullanıcılar"
          value={stats?.totalUsers.value}
          change={stats?.totalUsers.change}
          trend={stats?.totalUsers.trend}
          target={stats?.totalUsers.target}
          completion={stats?.totalUsers.completion}
          icon={Users}
          color="purple"
        />
        <AdvancedKPICard
          title="Dönüşüm Oranı"
          value={stats?.conversionRate.value}
          suffix="%"
          change={stats?.conversionRate.change}
          trend={stats?.conversionRate.trend}
          target={stats?.conversionRate.target}
          completion={stats?.conversionRate.completion}
          icon={Target}
          color="green"
        />
        <AdvancedKPICard
          title="Kullanıcı Başına Gelir"
          value={stats?.avgRevenuePerUser.value}
          change={stats?.avgRevenuePerUser.change}
          trend={stats?.avgRevenuePerUser.trend}
          currency={stats?.avgRevenuePerUser.currency}
          target={stats?.avgRevenuePerUser.target}
          completion={stats?.avgRevenuePerUser.completion}
          icon={DollarSign}
          color="blue"
        />
        <AdvancedKPICard
          title="Sistem Çalışma Süresi"
          value={stats?.systemUptime.value}
          suffix="%"
          change={stats?.systemUptime.change}
          trend={stats?.systemUptime.trend}
          target={stats?.systemUptime.target}
          completion={stats?.systemUptime.completion}
          icon={Zap}
          color="green"
        />
      </div>

      {/* Advanced Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Multi-metric Revenue & Signups Chart */}
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span>Gelir & Kayıt Analizi (Son 12 Hafta)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  Toplam Kayıt: {chartData?.signups?.reduce((sum, item) => sum + item.signups, 0) || 0}
                </Badge>
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  Toplam Gelir: ${chartData?.signups?.reduce((sum, item) => sum + item.revenue, 0)?.toLocaleString() || 0}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={chartData?.signups}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="week" 
                  stroke="#6b7280"
                  fontSize={12}
                />
                <YAxis yAxisId="left" stroke="#6b7280" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'signups') return [value, 'Yeni Kayıt'];
                    if (name === 'revenue') return [`$${value.toLocaleString()}`, 'Gelir'];
                    if (name === 'conversion') return [`${value}%`, 'Dönüşüm'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="signups" 
                  fill="url(#signupsGradient)" 
                  name="Yeni Kayıtlar"
                  radius={[4, 4, 0, 0]}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  name="Haftalık Gelir ($)"
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="conversion" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Dönüşüm Oranı (%)"
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                />
                <defs>
                  <linearGradient id="signupsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Enhanced Subscription Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PieChartIcon className="h-5 w-5 text-purple-600" />
              <span>Abonelik Dağılımı</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData?.subscriptions}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={false}
                >
                  {chartData?.subscriptions.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} kullanıcı`, 
                    `${props.payload.name} (${(value / chartData?.subscriptions.reduce((sum, item) => sum + item.value, 0) * 100).toFixed(1)}%)`
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-4">
              {chartData?.subscriptions.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-gray-700">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{item.value}</div>
                    <div className="text-xs text-gray-500">${item.revenue?.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-green-600" />
              <span>Coğrafi Dağılım</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData?.geography} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="country" type="category" width={80} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Kullanıcı Oranı']}
                />
                <Bar 
                  dataKey="value" 
                  fill="url(#geoGradient)"
                  radius={[0, 4, 4, 0]}
                />
                <defs>
                  <linearGradient id="geoGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.4}/>
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* System Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-orange-600" />
              <span>Sistem Performansı (24 Saat)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData?.performance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'cpu') return [`${value}%`, 'CPU Kullanımı'];
                    if (name === 'memory') return [`${value}%`, 'Bellek Kullanımı'];
                    if (name === 'requests') return [value, 'İstek/dk'];
                    return [value, name];
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="cpu" 
                  stackId="1"
                  stroke="#ef4444" 
                  fill="url(#cpuGradient)"
                  name="CPU Kullanımı"
                />
                <Area 
                  type="monotone" 
                  dataKey="memory" 
                  stackId="2"
                  stroke="#3b82f6" 
                  fill="url(#memoryGradient)"
                  name="Bellek Kullanımı"
                />
                <defs>
                  <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Organizations Table */}
      <Card className="shadow-sm border-0">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 rounded-t-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Son Kayıt Olan Organizasyonlar</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Aktif organizasyonlar ve durumları</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                Filtrele
              </Button>
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Dışa Aktar
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 text-blue-600 hover:text-blue-700">
                <ArrowUpRight className="h-4 w-4" />
                Tümünü Görüntüle
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="orgs-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Organizasyon</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Tip</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Plan</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">MRR</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Kayıt Tarihi</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Durum</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(recentOrgs.length > 0 ? recentOrgs : []).map((org, index) => (
                  <tr key={index} className="hover:bg-blue-50/30 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${
                          org.type === 'CONTRACTOR' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          org.type === 'EXHIBITOR' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                          'bg-gradient-to-br from-orange-500 to-orange-600'
                        }`}>
                          {org.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{org.name}</div>
                          <div className="text-sm text-gray-500">{org.users} kullanıcı • {org.lastActive}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge 
                        variant="outline" 
                        className={`font-medium ${
                          org.type === 'CONTRACTOR' ? 'text-blue-700 border-blue-200 bg-blue-50' :
                          org.type === 'EXHIBITOR' ? 'text-green-700 border-green-200 bg-green-50' :
                          'text-orange-700 border-orange-200 bg-orange-50'
                        }`}
                      >
                        {org.type}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <span className="capitalize font-medium text-gray-700">{org.plan}</span>
                        {org.plan === 'enterprise' && <Crown className="h-4 w-4 text-yellow-500" />}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-semibold text-gray-900">${org.mrr}</div>
                      <div className="text-sm text-gray-500">aylık</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-gray-700">
                        {org.date
                          ? new Date(org.date).toLocaleDateString('tr-TR', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })
                          : '—'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {org.date
                          ? `${Math.floor((new Date() - new Date(org.date)) / (1000 * 60 * 60 * 24))} gün önce`
                          : ''}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge 
                        variant={
                          org.status === 'active' ? 'default' : 
                          org.status === 'trial' ? 'secondary' :
                          'destructive'
                        }
                        className={`font-medium ${
                          org.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                          org.status === 'trial' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                          'bg-red-100 text-red-800 hover:bg-red-100'
                        }`}
                      >
                        {org.status === 'active' ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Aktif</>
                        ) : org.status === 'trial' ? (
                          <><Clock className="h-3 w-3 mr-1" /> Deneme</>
                        ) : (
                          <><AlertCircle className="h-3 w-3 mr-1" /> Beklemede</>
                        )}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-blue-100">
                          <Eye className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100">
                          <MoreHorizontal className="h-4 w-4 text-gray-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer with Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <div className="text-sm text-gray-500">
              Son {recentOrgs.length} kayıt gösteriliyor
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" disabled>
                Önceki
              </Button>
              <Button variant="outline" size="sm" className="bg-blue-600 text-white border-blue-600">
                1
              </Button>
              <Button variant="outline" size="sm">
                2
              </Button>
              <Button variant="outline" size="sm">
                3
              </Button>
              <Button variant="outline" size="sm">
                Sonraki
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UltraDashboard;